import React, { useEffect, useState } from 'react';

/**
 * **휴대폰 홈 화면에 추가 단추** (사용자 2026-10-27 "모바일 사이트에서 홈에 추가하기 버튼").
 *
 * 브라우저마다 되는 길이 다르다.
 *  - 안드로이드 크롬·삼성 인터넷: `beforeinstallprompt` 를 받아 두었다가 누르면 설치 창을 바로 띄운다.
 *    이 사건은 페이지가 뜨자마자 한 번만 오므로 `index.tsx` 에서 `captureInstallPrompt()` 로 먼저 받아 둔다(서랍이 열릴 때는 이미 지나갔다).
 *  - 아이폰 사파리: 사이트가 설치 창을 띄울 길이 없다 → 공유 단추 → "홈 화면에 추가" 순서를 보여 준다.
 *  - 카카오톡 등 앱 안 브라우저: 홈 화면 추가가 없다 → 크롬·사파리로 열라고 알린다.
 * 이미 홈 화면 앱으로 열린 경우(standalone)에는 단추를 숨긴다.
 */

let deferred: any = null;
const listeners = new Set<() => void>();

export function captureInstallPrompt() {
    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();             // 크롬의 자체 안내 띠 대신 우리 단추로 띄운다
        deferred = e;
        listeners.forEach((f) => f());
    });
    window.addEventListener('appinstalled', () => {
        deferred = null;
        listeners.forEach((f) => f());
    });
}

const isStandalone = () =>
    window.matchMedia?.('(display-mode: standalone)').matches || (navigator as any).standalone === true;

function guideFor(ua: string): { title: string; steps: string[] } {
    if (/KAKAOTALK|NAVER|Instagram|FBAN|FBAV|Line\//i.test(ua))
        return { title: '앱 안 브라우저에서는 홈 화면 추가가 안 돼요', steps: ['오른쪽 위(또는 아래) ⋮ 메뉴 → "다른 브라우저로 열기"', '크롬·사파리에서 이 단추를 다시 누르세요'] };
    if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1))
        return { title: '아이폰 · 아이패드', steps: ['사파리 아래(또는 위)의 공유 단추 (□↑) 누르기', '"홈 화면에 추가" 누르기', '오른쪽 위 "추가"'] };
    if (/SamsungBrowser/i.test(ua))
        return { title: '삼성 인터넷', steps: ['아래 ≡ 메뉴 누르기', '"현재 페이지 추가" → "홈 화면"'] };
    return { title: '크롬', steps: ['오른쪽 위 ⋮ 메뉴 누르기', '"홈 화면에 추가" 또는 "앱 설치" 누르기'] };
}

// 집 + 더하기 선 아이콘(Tabler home-plus) — 그림 글자(이모지)는 휴대폰마다 모양·색이 달라서 선 그림으로 넣고 글자색을 따른다 (사용자 2026-10-27 2번)
const HomePlusIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M19 12h2l-9 -9l-9 9h2v7a2 2 0 0 0 2 2h5.5" />
        <path d="M9 21v-6a2 2 0 0 1 2 -2h2a2 2 0 0 1 1.5 .67" />
        <path d="M16 19h6" />
        <path d="M19 16v6" />
    </svg>
);

const InstallAppButton: React.FC<{ style?: React.CSSProperties }> = ({ style }) => {
    const [, setTick] = useState(0);
    const [guide, setGuide] = useState(false);
    useEffect(() => {
        const f = () => setTick((t) => t + 1);
        listeners.add(f);
        return () => { listeners.delete(f); };
    }, []);
    if (isStandalone()) return null;

    const onClick = async () => {
        if (deferred) {
            const e = deferred;
            deferred = null;            // 설치 창은 한 번만 띄울 수 있다
            e.prompt();
            await e.userChoice.catch(() => null);
            setTick((t) => t + 1);
            return;
        }
        setGuide(true);
    };
    const g = guideFor(navigator.userAgent);

    return (
        <>
            <button
                onClick={onClick}
                style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    textAlign: 'left', padding: '10px 12px', border: '1px dashed rgba(102, 126, 234, 0.45)', borderRadius: 10,
                    background: 'rgba(102, 126, 234, 0.06)', color: '#5a67d8', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    ...style
                }}
            >
                <HomePlusIcon />
                홈 화면에 추가
            </button>
            {guide && (
                <div
                    onClick={() => setGuide(false)}
                    style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(20, 24, 40, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ background: '#fff', borderRadius: 16, padding: '18px 18px 14px', width: 'min(320px, 100%)', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}
                    >
                        <div style={{ fontSize: 15, fontWeight: 900, color: '#333', marginBottom: 4 }}>홈 화면에 추가</div>
                        <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>{g.title}</div>
                        <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#444', lineHeight: 1.8 }}>
                            {g.steps.map((s) => <li key={s}>{s}</li>)}
                        </ol>
                        <button
                            onClick={() => setGuide(false)}
                            style={{ marginTop: 14, width: '100%', padding: '10px', border: 'none', borderRadius: 10, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}
                        >
                            확인
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default InstallAppButton;
